import { z } from "zod";
import { limits } from "./limits.js";
import { easingSchema, pointSchema, rectSchema } from "./primitives.js";

export const actionTypes = ["click", "typing", "scroll", "navigation", "selection", "hover", "unknown"] as const;

/** Where the action came from. Manual always wins over anything AI-derived. */
export const actionSources = ["metadata", "vision", "manual"] as const;

/**
 * What an effect does. Four, where there were seven.
 *
 * Several of the old names were the same effect with a setting changed —
 * `spotlight` was a highlight with its surroundings dimmed, `zoom-cursor` was a
 * zoom that also moved the pointer. Offering them as separate modes made you
 * choose between things you could not tell apart, and hid the setting that
 * actually differed. They are `dim` and `cursor` now.
 *
 * `none` is gone too: an effect that does nothing is one you deleted, and the
 * layer can express that.
 */
export const focusModes = ["zoom", "highlight", "callout", "cursor", "blur"] as const;

/** What the renderer has always drawn for `spotlight`, kept so nothing shifts. */
const LEGACY_SPOTLIGHT_DIM = 0.62;

/**
 * Reads an effect written under the old names.
 *
 * On read rather than as a migration, because a timeline is a document: an
 * export from last month has to open, and rewriting every stored row to change
 * two words is a worse trade than translating them as they arrive.
 */
const fromLegacyMode = (value: unknown): unknown => {
  if (!value || typeof value !== "object") return value;
  const raw = value as Record<string, unknown>;

  if (raw.mode === "spotlight") return { ...raw, mode: "highlight", dim: raw.dim ?? LEGACY_SPOTLIGHT_DIM };
  if (raw.mode === "zoom-cursor") return { ...raw, mode: "zoom", cursor: raw.cursor ?? true };
  // Only ever a default, never stored on an effect — but a stored one would
  // otherwise fail the whole timeline rather than this one field.
  if (raw.mode === "none") return { ...raw, mode: "zoom" };
  return value;
};

export const actionSchema = z.object({
  id: z.string().min(1).max(64),
  /** Seconds into the BODY recording. */
  timestamp: z.number().nonnegative().max(limits.maxVideoDurationSeconds),
  type: z.enum(actionTypes),
  source: z.enum(actionSources).default("vision"),
  target: rectSchema.optional(),
  label: z.string().max(280).optional(),
  confidence: z.number().min(0).max(1).optional(),
});

/**
 * The rendered consequence of an action. Focus effects are stored explicitly on
 * the timeline rather than recomputed at render time, so the editor preview and
 * the final render always agree.
 */
export const focusEffectSchema = z.preprocess(
  fromLegacyMode,
  z
    .object({
      id: z.string().min(1).max(64),
      actionId: z.string().min(1).max(64).optional(),
      mode: z.enum(focusModes),
      /** Seconds into the BODY section. */
      start: z.number().nonnegative(),
      duration: z.number().gt(0).max(30),
      /**
       * What the effect points at.
       *
       * Optional, because "not aimed yet" is a real state: an effect added from
       * the timeline has no region until someone draws one, and inventing a
       * rectangle in the middle of the frame is how zoom, highlight and the
       * cursor all ended up pointing at nothing in particular.
       */
      target: rectSchema.optional(),
      zoom: z.number().min(1).max(4).default(1.35),
      /** How far the surroundings are darkened. 0 is a plain outline. */
      dim: z.number().min(0).max(0.8).default(0),
      /**
       * Blur radius over the region, in composition pixels. Only `blur` uses it.
       *
       * The minimum is 4 rather than 0 on purpose. This is the one effect whose
       * job is to hide something — a customer's name, an address, a key on a
       * real dashboard — and a one-pixel blur is legible while looking like the
       * job was done. If the intent is not to obscure, the mode is wrong, not
       * the radius.
       */
      blur: z.number().min(4).max(80).default(18),
      /** Whether the pointer travels to the target. Only zoom and cursor use it. */
      cursor: z.boolean().default(false),
      /**
       * How long the effect takes to arrive and to leave, in seconds.
       *
       * Durations rather than a fraction of the effect's own length: a third of
       * a one-second highlight is a flicker and a third of a ten-second zoom is
       * a drift, and neither is what "ease in" means. Both are clamped to fit
       * when they are longer than the effect itself.
       *
       * Zero on either end is a hard cut, which is occasionally what you want.
       */
      easeIn: z.number().min(0).max(4).default(0.5),
      easeOut: z.number().min(0).max(4).default(0.5),
      easing: easingSchema.default("ease-in-out"),
      /** Cursor travel, only meaningful for cursor-bearing modes. */
      cursorFrom: pointSchema.optional(),
      click: z.boolean().default(false),
      label: z.string().max(280).optional(),
    })
    .refine((f) => f.mode !== "callout" || Boolean(f.label), "callout focus requires a label to display"),
);

export type Action = z.infer<typeof actionSchema>;
export type FocusEffect = z.infer<typeof focusEffectSchema>;
export type FocusMode = (typeof focusModes)[number];
export type ActionType = (typeof actionTypes)[number];
